import { useEffect } from 'react'

const POSTHOG_API_HOST = 'https://eu.i.posthog.com'
const POSTHOG_UI_HOST = 'https://eu.posthog.com'

// Text inside elements marked like this is masked in session replays.
// (`ph-no-capture` hides an element from replay and autocapture completely.)
export const POSTHOG_SENSITIVE_SELECTOR = '[data-sensitive], .ph-sensitive'

/**
 * PostHog for the consumer-facing Journey app (anonymous EU visitors).
 *
 * - Disabled entirely (posthog-js is never even downloaded) when REACT_APP_POSTHOG_KEY is empty.
 * - Cookieless: `persistence: 'memory'`, nothing written to cookies or localStorage, so no
 *   consent banner is needed for it; every page load is a new anonymous visitor.
 * - No person profiles (`identified_only` and the app never identifies anyone).
 * - Error tracking on (`capture_exceptions`).
 * - Session replay OFF unless REACT_APP_POSTHOG_REPLAY=true at build time; when on, all inputs
 *   and any text marked sensitive are masked.
 */
export const usePosthogSetup = () => {
  useEffect(() => {
    const key = process.env.REACT_APP_POSTHOG_KEY
    if (!key) return

    const replayEnabled = process.env.REACT_APP_POSTHOG_REPLAY === 'true'
    const environment =
      process.env.REACT_APP_BRANCH_NAME === 'prod'
        ? 'production'
        : process.env.REACT_APP_BRANCH_NAME === 'test'
          ? 'test'
          : 'development'

    let cancelled = false
    import('posthog-js')
      .then(({ default: posthog }) => {
        if (cancelled || posthog.__loaded) return

        posthog.init(key, {
          api_host: POSTHOG_API_HOST,
          ui_host: POSTHOG_UI_HOST,
          persistence: 'memory',
          person_profiles: 'identified_only',
          capture_pageview: 'history_change', // react-router (pushState) navigation
          capture_pageleave: true,
          capture_exceptions: true,
          disable_session_recording: !replayEnabled,
          session_recording: {
            maskAllInputs: true,
            maskTextSelector: POSTHOG_SENSITIVE_SELECTOR,
          },
          disable_surveys: true,
        })
        posthog.register({ app: 'journey', environment })
      })
      .catch(() => {
        // Analytics must never break the app (e.g. the chunk is blocked by an ad blocker).
      })

    return () => {
      cancelled = true
    }
  }, [])
}
